import React, { useEffect, useMemo, useState } from "react";
import Container from "../hoc/Container";
import {
  BanknotesIcon,
  FireIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import SelectField from "../components/common/form/selectField";
import pspricesDealsService from "../service/pspricesDeals.service";

const PAGE_SIZE = 12;

function PsPricesDealsPage({
  title,
  regions,
  platforms,
  platformQuery,
  renderButtons,
  ymPath,
}) {
  const [region, setRegion] = useState(regions[0]?.value);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const moreUrl = useMemo(
    () => pspricesDealsService.buildCollectionUrl(region, platformQuery),
    [region, platformQuery],
  );

  const pageCount = Math.max(1, Math.ceil(deals.length / PAGE_SIZE));
  const pageDeals = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return deals.slice(start, start + PAGE_SIZE);
  }, [deals, currentPage]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await pspricesDealsService.getDeals(region, platforms);
        if (cancelled) {
          return;
        }
        setDeals(data.results);
        setCurrentPage(1);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error("Error loading psprices deals:", err);
        setError("Ошибка загрузки данных о скидках");
        setDeals([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [region, platforms]);

  useEffect(() => {
    if (ymPath && window.ym) {
      window.ym(93997310, "hit", ymPath, {
        params: {
          title,
          referer: "https://trendy-eng-shop.ru/",
        },
      });
    }
  }, [ymPath, title]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [currentPage, region]);

  const handleRegionChange = (e) => {
    setRegion(e.value);
  };

  return (
    <Container>
      <div className="content-block">
        <h1 className="head-1">{title}</h1>

        <section className="mx-5 md:flex md:items-end md:justify-between">
          <div>
            <p className="mb-2 mt-4 text-center text-xl">Коды пополнения:</p>
            {typeof renderButtons === "function" ? renderButtons(region) : null}
          </div>
          {regions.length > 1 ? (
            <div className="dark:dark-text my-2 flex justify-center">
              <SelectField
                label="Регион"
                value={region}
                onChange={handleRegionChange}
                options={regions}
                name="region"
              />
            </div>
          ) : null}
        </section>

        {loading ? (
          <div className="mt-8 text-center text-2xl">Загрузка скидок...</div>
        ) : error ? (
          <h3 className="mt-8 text-center text-2xl text-red-500">{error}</h3>
        ) : (
          <>
            <section className="m-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3 lg:grid-cols-4">
              {pageDeals.map((deal) => (
                <div key={deal.id}>
                  <a href={deal.link} target="_blank" rel="noreferrer">
                    <div className="relative">
                      <img
                        src={
                          deal.cover ||
                          "https://via.placeholder.com/320x180?text=No+Image"
                        }
                        alt={deal.name}
                        className="aspect-[4/3] w-full rounded object-cover transition hover:scale-105"
                      />
                      {deal.discountPercent != null ? (
                        <div className="absolute bottom-0 left-0 rounded-sm bg-white p-1 font-mono text-sm text-black">
                          <FireIcon className="inline-block w-4 text-orange-500" />
                          {deal.discountPercent}%
                        </div>
                      ) : null}
                    </div>
                  </a>
                  <div className="flex justify-between pt-2">
                    <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs leading-tight sm:text-sm">
                      <BanknotesIcon className="inline-block w-3 shrink-0 text-green-500" />
                      <span className="font-bold">{deal.price}</span>
                      {deal.originalPrice ? (
                        <span className="text-gray-500 line-through">
                          {deal.originalPrice}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {deal.discountEndDate ? (
                    <p className="pb-2 text-xs">
                      <ClockIcon className="mr-2 inline-block w-3.5 text-red-500" />
                      до{" "}
                      {new Date(deal.discountEndDate).toLocaleDateString("ru", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  ) : null}
                  <a href={deal.link} target="_blank" rel="noreferrer">
                    <h2 className="text-sm font-light hover:underline">
                      {deal.name}
                    </h2>
                  </a>
                </div>
              ))}
            </section>

            <p className="dark:dark-text mb-1 text-center text-xs">
              {deals.length
                ? `${1 + PAGE_SIZE * (currentPage - 1)} - ${Math.min(
                    PAGE_SIZE * currentPage,
                    deals.length,
                  )} из ${deals.length}`
                : "Нет скидок"}
            </p>

            <div className="mt-4 flex w-full flex-wrap justify-center gap-2">
              {[1, 2].slice(0, pageCount).map((page) => (
                <button
                  key={`page-${page}`}
                  type="button"
                  className={currentPage === page ? "pagItemActive" : "pagItem"}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <a
                href={moreUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Больше скидок
              </a>
            </div>

            <div className="mt-10 text-center text-xs">
              * Данные по скидкам получены через API сайта{" "}
              <a
                href="https://psprices.com"
                className="text-blue-700 dark:text-indigo-400"
                target="_blank"
                rel="noreferrer"
              >
                psprices.com
              </a>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}

export default PsPricesDealsPage;
